import { Typography } from "@mui/material";
import type { ReactNode } from "react";
import styled from "styled-components";
import { Resources, useResource } from "Translations/Resources";

type Props = {
  number: number;
  title: string;
  subtitle?: string;
  isOptional?: boolean;
  /** The last step has no connector line running below its badge. */
  isLast?: boolean;
  children: ReactNode;
};

const BADGE_SIZE = 36;

const StyledStep = styled.div`
  display: grid;
  grid-template-columns: ${BADGE_SIZE}px 1fr;
  column-gap: ${({ theme }) => theme.spacing(3)};

  ${({ theme }) => theme.breakpoints.down("md")} {
    grid-template-columns: 1fr;
  }
`;

const StyledRail = styled.div<{ $isLast: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};

  &::after {
    content: "";
    flex: 1;
    width: 2px;
    background-color: ${({ theme }) => theme.colors.border};
    display: ${({ $isLast }) => ($isLast ? "none" : "block")};
  }

  ${({ theme }) => theme.breakpoints.down("md")} {
    display: none;
  }
`;

const StyledBadge = styled.span<{ $isOptional: boolean }>`
  width: ${BADGE_SIZE}px;
  height: ${BADGE_SIZE}px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  font-weight: 600;
  font-size: 14px;
  color: ${({ theme, $isOptional }) =>
    $isOptional
      ? theme.palette.text.primary
      : theme.palette.primary.contrastText};
  background-color: ${({ theme, $isOptional }) =>
    $isOptional ? theme.palette.grey[200] : theme.palette.primary.main};
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};

  /* On desktop the badge lives in the rail; inline it only when the rail is hidden. */
  & > ${StyledBadge} {
    display: none;
  }

  ${({ theme }) => theme.breakpoints.down("md")} {
    & > ${StyledBadge} {
      display: inline-flex;
    }
  }
`;

/* The step gap is the body's padding so the rail's connector runs through it unbroken. */
const StyledBody = styled.div<{ $isLast: boolean }>`
  display: grid;
  row-gap: ${({ theme }) => theme.spacing(2)};
  min-width: 0;
  padding-bottom: ${({ theme, $isLast }) => theme.spacing($isLast ? 0 : 5)};

  ${({ theme }) => theme.breakpoints.down("md")} {
    padding-bottom: ${({ theme, $isLast }) => theme.spacing($isLast ? 0 : 4)};
  }
`;

/** Lines content up with the step bodies, for things rendered outside the steps (footer, actions). */
export const StoryFormStepIndent = styled.div`
  padding-left: ${({ theme }) => `calc(${BADGE_SIZE}px + ${theme.spacing(3)})`};

  ${({ theme }) => theme.breakpoints.down("md")} {
    padding-left: 0;
  }
`;

/** One numbered section of the story form: badge + rail on desktop, inline badge on phones. */
export const StoryFormStep = ({
  number,
  title,
  subtitle,
  isOptional = false,
  isLast = false,
  children,
}: Props) => {
  const { t } = useResource();

  const badge = <StyledBadge $isOptional={isOptional}>{number}</StyledBadge>;

  return (
    <StyledStep>
      <StyledRail $isLast={isLast}>{badge}</StyledRail>
      <StyledBody $isLast={isLast}>
        <div>
          <StyledHeader>
            {badge}
            <Typography variant="h6" component="h2" fontWeight={600}>
              {title}
              {isOptional && (
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  ml={1.5}
                >
                  {`·\u00A0\u00A0${t(Resources.Common.Optional)}`}
                </Typography>
              )}
            </Typography>
          </StyledHeader>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </div>
        {children}
      </StyledBody>
    </StyledStep>
  );
};
